import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'primereact/panel';
import { useHistory, useParams } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment-timezone';
import { Button } from 'primereact/button';
import AssessmentUtil from '../../../utils/AssessmentUtil';
import ReportPanelContainer from './ReportPanel';
import { AssessmentConstants, AppConstants } from '../../../shared/Constants';
import ExtendTime from './ExtendTime';
import { Loading } from '../../../components/Loading';
import { growlErrors } from '../../../utils/NotificationUtil';
import AssessmentDetailInfo from './AssessmentDetailInfo';
import assessmentDetailQuery from './AssessmentDetailQuery';
import { DeleteTextButton } from '../../../components/button/ActionButton';
import { removeAssessment } from '../../../services/apps/AssessmentServices';
import AsmtTimeLineBox from './AsmtTimelineBox';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlShape } from '../../../components/prop-types';
import { withAccount } from '../../../components/AccountContext';
import AssessmentDeleteConfirmForm from './AssessmentDeleteConfirmForm';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const i18n = defineMessages({
  deleteWarning: {
    id: 'assessment.delete.warning',
    defaultMessage: 'This assessment will automatically be deleted on: ',
  },
});

const AssessmentDetail = ({ currentAccount, data, growl }) => {
  const [isDisabledExpiration, setIsDisabledExpiration] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const history = useHistory();
  const { aid, assessmentId } = useParams();
  const { formatMessage, formatDate, timeZone } = useIntl();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const { loading, account } = data;

  if (loading) {
    return <Loading />;
  }

  const { assessment } = account;

  const { id, status, respondent, createdOn, expiredOn, scheduledForDeletionOn } = assessment;
  const { firstName, lastName } = respondent;

  const processDeletingAssessment = async (formikValues) => {
    const { confirmation_code, re_confirmation_code } = formikValues;

    if (assessmentId && confirmation_code === re_confirmation_code) {
      try {
        await removeAssessment({
          assessmentId,
          accountId: currentAccount,
        });

        setShowDeleteConfirmModal(false);

        const listURL = `/accounts/${currentAccount}/assessments`;
        history.push(listURL);
      } catch (error) {
        growlErrors(growl, error);
      }
    }
  };

  const updateExpiration = (event) => {
    if (assessment) {
      setIsDisabledExpiration(true);

      const formattedExpiration = moment(event.value).format(AppConstants.DEF_LOCAL_DATETIME_FORMAT);
      AssessmentUtil.updateExpiration({
        accountId: aid,
        assessmentId: id,
        expirationTime: formattedExpiration,
      })
        .then(() => {
          setIsDisabledExpiration(false);
        })
        .catch((graphqlResponse) => {
          setIsDisabledExpiration(false);
          growlErrors(growl, graphqlResponse);
        });
    }
  };

  const reportPanel =
    status === AssessmentConstants.ASMT_STATUS.SUBMITTED ? (
      <ReportPanelContainer
        respondent={respondent}
        assessment={assessment}
        jobProfiles={assessment.compatibleJobProfiles}
      />
    ) : null;
  const specTz = timeZone || 'UTC';
  const dateWithTz = moment(expiredOn || new Date())
    .tz(specTz)
    .format(AppConstants.DEF_LOCAL_DATETIME_FORMAT);
  const dateValue = new Date(dateWithTz);
  const expendTimeContainer = AssessmentUtil.isTimeExtendable(assessment) ? (
    <ExtendTime
      isDisabled={isDisabledExpiration}
      createdAt={createdOn}
      onUpdateExpiration={updateExpiration}
      dateValue={dateValue}
    />
  ) : null;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <div className="p-col-12 align-right">
              {scheduledForDeletionOn ? (
                <Button
                  disabled
                  icon="pi pi-info"
                  className="p-button-warning"
                  label={
                    formatMessage(i18n.deleteWarning) +
                    formatDate(new Date(scheduledForDeletionOn), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }
                />
              ) : (
                <DeleteTextButton
                  position="right"
                  onClick={() => setShowDeleteConfirmModal(true)}
                  disabled={subscriptionIsExpired}
                  text={formatMessage({ id: 'del_header.asmt', defaultMessage: 'Delete Assessment' })}
                />
              )}
            </div>
            <div className="p-col-12">
              <Panel header={`${firstName} ${lastName}`}>
                <div className="p-grid">
                  <div className="p-col-12 p-md-6">
                    <AssessmentDetailInfo assessment={assessment} expendTimeContainer={expendTimeContainer} />
                  </div>
                  <div className="p-col-12 p-md-6">
                    <AsmtTimeLineBox asmt={assessment} />
                  </div>
                </div>
              </Panel>
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col">{reportPanel}</div>
          </div>
        </div>
        <AssessmentDeleteConfirmForm
          isVisible={showDeleteConfirmModal}
          cancelDelete={() => setShowDeleteConfirmModal(false)}
          confirmDelete={processDeletingAssessment}
          assessmentId={assessmentId}
        />
      </div>
    </div>
  );
};

AssessmentDetail.propTypes = {
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    assessment: PropTypes.object,
    error: PropTypes.object,
    account: PropTypes.shape({
      assessment: PropTypes.shape({
        id: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        respondent: PropTypes.shape({
          firstName: PropTypes.string.isRequired,
          lastName: PropTypes.string.isRequired,
        }).isRequired,
        expiredOn: PropTypes.objectOf(PropTypes.any).isRequired,
        createdOn: PropTypes.objectOf(PropTypes.any).isRequired,
        scheduledForDeletionOn: PropTypes.objectOf(PropTypes.any).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

const AssessmentDetailGraphql = assessmentDetailQuery(withAccount(AssessmentDetail));
export default withGrowl(AssessmentDetailGraphql);
